import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useRef, useEffect } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { DexaIntro } from "./dexaIntro.component.js"
import { ConfirmDexa } from "./confirmDexa.component.js"
import { listProductTypes } from '../../services/productType.service'
import MembershipTypeCode from "../../enums/membershipTypeCode.enum.js"
import { getMembershipTypeByCode} from '../../services/membershipType.service'
import { completeFlow } from "../../services/flow.service.js"
import { Button } from "antd"
import UserHelper from "../../helpers/user.helper"
import ProductHelper from "../../helpers/product.helper"
import Panels from "../panels/panels.component"
import FieldType from "../../enums/fieldType.enum"
import PayTo from "../../enums/payTo.enum"
export const Step = {
  INTRO: 'intro',
  LOCATION: 'location',
  FACILITY: 'facility',
  BASIC_INFO: 'basic-info',
  PREGNANCY: 'pregnancy',
  DISQUALIFIED: 'disqualified',
  CONTACT: 'contact',
  PAY: 'pay',
  ADDITIONAL_INFO: 'additional-info',
  PASSWORD: 'password',
  CONFIRM: 'confirm',

}

export const DexaBodyCompFlow = () => {
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [productTypes, setProductTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [membershipType, setMembershipType] = useState()
  const [selectedMenuItem, setSelectedMenuItem] = useState()
  const [initialUser, setInitialUser] = useState()

  useEffect(() => {
    fetchSkeleton()
  }, [hasPass, initialUser, membershipType, productTypes, instalabMembership, flow])

  useEffect(() => {
    fetchProductTypes()
    fetchMembershipType()
  }, [])

  useEffect(() => {
    // Reset when flow ID changes (new flow started)
    if (flow?._id) {
      hasCompletedFlowRef.current = false;
    }
  }, [flow?._id]); 


  useEffect(() => {
    if (flow?.facility && flow.facility.menu) {
      const product = flow.facility.menu.find(f => f.productType === getProductType(ProductTypeCode.DEXA_BODYCOMP)?._id);
      setSelectedMenuItem(product);
    }
  }, [flow, productTypes]);

  

  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.DEXA_BODYCOMP,
            ProductTypeCode.CONCIERGE_FEE,
          ]
        }
      }
    })
    setProductTypes(response)
  }

  const fetchMembershipType = async () => {
    const response = await getMembershipTypeByCode(MembershipTypeCode.LONGEVITY)
    setMembershipType(response)
  }

  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

  const instructions = [
    {
      sections: [{
        title: 'Schedule Your Scan',
        description: <>Our concierge team will reach out to schedule your scan at your chosen imaging center.</>
      }, 
    ]
    }, 
    {
      sections: [{
        title: 'Complete the Scan',
        description: <>Wear comfortable clothing. The scan takes about 15 minutes, and results are usually available immediately after your appointment.</>
      }]
    },
    {
      sections: [{
        title: 'Review Your Results',
        description: <>Instalab will review your results and provide personalized recommendations.</>
      }]
    }]

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return

    const hasCredit = UserHelper.hasCredit(initialUser, ProductTypeCode.DEXA_BODYCOMP)

    const totalCost = ProductHelper.getTotalCostV2({
      productTypes: [getProductType(ProductTypeCode.DEXA_BODYCOMP)],
      instalabMembership,
      currentUser,
      priceOverride: selectedMenuItem?.payTo === PayTo.INSTALAB ? [{
        code: ProductTypeCode.DEXA_BODYCOMP,
        price: selectedMenuItem?.price
      }] : null
    })

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.CUSTOM,
        nextStep: Step.LOCATION,
        content: ({ footer }) => <DexaIntro 
          footer={footer} 
          instructions={instructions} 
          productType={getProductType(ProductTypeCode.DEXA_BODYCOMP)} 
          patient={initialUser}
        />
      },

      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        buildProduct:true,
        nextStep: Step.FACILITY,
        title: `What's your current address?`,
        description: `We'll match you to nearby imaging centers for your DEXA scan.`,
        field: {
          name: 'location'
        },
      },
      [Step.FACILITY]: {
        type: StepType.FACILITY,
        nextStep: Step.BASIC_INFO,
        buildFlow: true,
        productTypeCode: [ProductTypeCode.DEXA_BODYCOMP],
        showPrice: !hasCredit,
        title: `Choose your preferred location.`,
        description: `We partner with top-rated imaging centers. Select the one that works best for you.`,
        field: {
          name: 'facility',
          required: true,
        },
        onSuccess: (_, flow) => {
          setSelectedMenuItem(flow?.facility?.menu?.find(f => f.productType === getProductType(ProductTypeCode.DEXA_BODYCOMP)?._id))
        }
      },

      [Step.BASIC_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        onNextStep: (user) => {
          if (user?.gender === Gender.FEMALE) {
            return Step.PREGNANCY
          }
          else {
            return Step.CONTACT
          }
        },
        buildUser: true,
        title: `Let's get to know you.`,
        description: `We just need a few details to prepare your scan order and book your appointment at ${flow?.facility?.name}.`,
        fields: [
          {
            type: "group",
            label: "Full Name",
            required: true,
            fields: [
              {
                name: 'firstName',
                placeholder: 'First Name',
                label: 'First Name',
                required: true,
                type: 'text',
              },  
              {
                name: 'lastName',
                placeholder: 'Last Name',
                label: 'Last Name',
                required: true,
                type: 'text',
              },
            ]
          },
          {
            name: 'dob',
            label: 'Date of Birth',
            required: true,
            type: 'date',
            placeholder: 'MM/DD/YYYY',
          },
          {
            name: 'gender',
            label: 'Biological Sex',
            required: true,
            type: 'select',
            multiple:false,
            options: [
              {
                label: 'Male',
                value: Gender.MALE,
              }, {
                label: 'Female',
                value: Gender.FEMALE,
              }
            ],
          },
        ],
        skipIf: () => {
          return hasPass && (initialUser?.gender && initialUser?.dob && initialUser?.firstName && initialUser?.lastName)
        }
      },

      [Step.PREGNANCY]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: (user) => {
          if (user?.isPregnant) {
            return Step.DISQUALIFIED
          }
          else {
            return Step.CONTACT
          }
        },
        buildUser: true,
        title: `Are you currently pregnant?`,
        description: 'You should not get this test done now if there is any chance you may be pregnant.',
        field: {
          name: 'isPregnant',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
        skipIf: () => {
          return initialUser?.gender === Gender.MALE
        }
      },

      [Step.DISQUALIFIED]: {
        type: StepType.STATIC,
        title: `Sorry, you shouldn't get a DEXA scan at this time.`,
        description: () => {
          return <>This test uses CT imaging with radiation, which is not recommended during pregnancy due to potential risk to the developing fetus. It’s best to wait until after pregnancy unless absolutely necessary. Please contact us if you need more information.</>
        },
        buttonText: 'Get Help',
        path: '/help',
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <div>
          <Button
            className="question-btn"
            onClick={() => window.FrontChat("show")}
          >
            I have a question?
          </Button>

          <Panels excludeCodes={[ProductTypeCode.CTCALCIUM]}/>

          
        </div>
      },

      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PAY,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your appointment confirmation, directions and results here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return hasPass && (initialUser?.email && initialUser?.phone)
        }
      },

      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.ADDITIONAL_INFO,
        addProduct: true,
        priceOverride: () => {
          return (selectedMenuItem?.payTo === PayTo.INSTALAB)? [{
            code: ProductTypeCode.DEXA_BODYCOMP,
            price: selectedMenuItem?.price
          }] : null
        },
        productType: [ProductTypeCode.DEXA_BODYCOMP],
        title: () => {


          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {
            return  <>Pay <span className="true-price">${totalCost}</span> for your <span className="product-name">{getProductType(ProductTypeCode.DEXA_BODYCOMP).title}</span>.</>
          }
          else {
            return  <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">{getProductType(ProductTypeCode.CONCIERGE_FEE).title.toLowerCase()}</span> to get started.</>
          }
        },
        description: () => {
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {  
            return <>Includes everything: appointment scheduling, the scan itself, and a detailed report with your results.</>
          }
          else {
            return <>This fee covers your appointment scheduling, and a detailed results report. {!hasCredit && <p>Note: The remaining {selectedMenuItem?.price && ` $${selectedMenuItem?.price}`} will be paid directly to {flow?.facility ? flow?.facility.name : 'the facility'} at time of your appointment.</p>} </>
          }
        },
        skipIf: () => {
          return (totalCost === 0) || hasCredit
        },
      },

      [Step.ADDITIONAL_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        title: `One last step to finalize your scan order.`,
        description: `We need a few final details to complete your order and ensure scan safety.`,
        fields: [

          {
            type: FieldType.GROUP,     
            required: true,
            label: <span className="highlight-label">What is your height and weight?</span>,
            fields: [
              {
                name: 'height',
                placeholder: 'Enter your height ...',
                required: true,
                type: 'text',
                unit: 'inches',
              },  
              {
                name: 'weight',
                placeholder: 'Enter your weight ...',
                required: true,
                type: 'text',
                unit: 'lbs',
              },
            ]
          },
          
          {
            type: FieldType.TEXTAREA,
            name: 'freeformMedications',
            label: <span className="highlight-label">List any medications you currently take.</span>,
            placeholder: 'Separate each medication with a new line.',
          },         

        ],
      },

    
      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Create your password.`,
        description: `This lets you securely access your results, manage appointments, and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        showFooter: false,
        title: `All set!`,
        description: `We'll email you within 1 business day to schedule your scan.`,
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <ConfirmDexa instructions={instructions}/>,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        },
        
      },


    })
  }

  return <>
    <Flow 
      skeleton={skeleton} 
      flow={flow}
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      // productTypeCode={ProductTypeCode.DEXA_BODYCOMP}
    />

  </>
}